import { Pipe, PipeTransform } from '@angular/core';

import { marked, Marked, Tokens } from 'marked';
import DOMPurify from 'dompurify';

const EXTERNAL_LINK_PATTERN = /^https?:\/\//i;

/**
 * Dedicated v2 instance: full GFM (tables/lists) plus a link renderer that opens
 * external links in a new tab with `rel="noopener noreferrer"`. Kept separate so
 * the v1 inline path keeps using the default `marked` untouched.
 */
const blockMarked = new Marked({
  gfm: true,
  breaks: true,
  renderer: {
    link({ href, title, tokens }: Tokens.Link): string {
      const text = this.parser.parseInline(tokens);
      const titleAttr = title ? ` title="${title}"` : '';
      const safetyAttrs = EXTERNAL_LINK_PATTERN.test(href)
        ? ' target="_blank" rel="noopener noreferrer"'
        : '';

      return `<a href="${href}"${titleAttr}${safetyAttrs}>${text}</a>`;
    },
  },
});

@Pipe({
  standalone: true,
  name: 'parseMarkdown',
  pure: true,
})
export class ParseMarkdownPipe implements PipeTransform {
  transform(value: string, block = false): string {
    if (block) {
      const html = blockMarked.parse(value, { async: false });

      return DOMPurify.sanitize(html, { ADD_ATTR: ['target', 'rel'] });
    }

    value = value
      .replace(/\n\n<ul>/gi, '<ul>')
      .replace(/<\/ul>\n\n/gi, '\n</ul>')
      .replace(/<ol>\n/gi, '<ol>')
      .replace(/<\/ol>\n/gi, '</ol>')
      .replace(/<\/li>\n/gi, '</li>');

    return marked.parseInline(value, { async: false });
  }
}
